import * as React from 'react';

import { connect } from 'react-redux';

import { Error } from 'components/error/Error';
import Form from 'components/form/Form';
import { STATUSES } from 'core/api';
import { IReduxStoreState } from 'core/store/interfaces';

import { fetchHashtags } from '../hashtags/redux';
import { decorators, formConfig } from './formConfig';
import { IProps, IStateProps } from './interfaces';
import {
  createArticle,
  editArticle,
  fetchArticle,
  resetArticleForm,
} from './redux';

import { fetchCategories } from '../categories/redux';
import { FormLoader } from '../../../components/form/FormLoader';

const initialValues = {
  title: '',
  slug: '',
  isPublished: false,
  isSlugNotSynchronized: false,
  content: null,
  categoryId: null,
  hashtags: null,
  widgets: null,
  preview: null,
  permission: {
    countries: null,
    departments: null,
    seniorities: null,
    teams: null,
    titles: null,
    workplaces: null,
  },
};

class ArticleForm extends React.Component<IProps> {
  public componentDidMount(): void {
    const {
      match: {
        params: { id },
      },
    } = this.props;

    this.props.fetchCategories({ search: '?limit=100' });
    this.props.fetchHashtags({ search: '?limit=1000', hash: '' });

    if (id !== 'new') {
      this.props.fetchArticle(id);
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    const {
      match: {
        params: { id },
      },
    } = this.props;

    if (id === prevProps.match.params.id) {
      return;
    }

    if (!id || id === 'new') {
      this.props.resetArticleForm();
    } else {
      this.props.fetchArticle(id);
    }
  }

  public componentWillUnmount() {
    this.props.resetArticleForm();
  }

  public render() {
    const {
      article: {
        error,
        status,
        data: { data: article },
      },
      categories,
      hashtags,
      match: {
        params: { id },
      },
    } = this.props;

    if (status === STATUSES.FAILED) {
      return <Error>{error}</Error>;
    }

    if (
      (status === STATUSES.INITIAL && id === 'new') ||
      status === STATUSES.SUCCESS
    ) {
      return (
        <Form
          key={article ? 'update' : 'create'}
          mode={article ? 'update' : 'create'}
          {...this.props}
          config={formConfig}
          initialValues={article || initialValues}
          onSubmit={this.onSubmit}
          validate={this.validate}
          categories={(categories.data && categories.data.items) || []}
          withTranslation="article"
          withPreview="article"
          hashtags={
            hashtags.status === STATUSES.SUCCESS ? hashtags.data.items : []
          }
          decorators={decorators(article || initialValues)}
        />
      );
    }

    return <FormLoader config={formConfig} />;
  }

  private readonly onSubmit = async (values: any) => {
    const {
      history,
      match: {
        params: { id },
      },
    } = this.props;

    const valuesToSubmit = {
      categoryId: values.categoryId || null,
      content: values.content || '',
      featuredMedia: values.featuredMedia || null,
      hashtags: values.hashtags || [],
      id: values.id,
      isPublished: values.isPublished || false,
      permission: values.permission || null,
      preview: values.preview || '',
      publishDate: values.publishDate || null,
      slug: values.slug || '',
      title: values.title,
      translation: values.translation || null,
      unpublishDate: values.unpublishDate || null,
      widgets: values.widgets || [],
    };

    if (id === 'new') {
      await this.props.createArticle(valuesToSubmit);
    } else {
      await this.props.editArticle(id, valuesToSubmit);
    }

    history.push('/admin/articles');

    return true;
  };

  private readonly validate = async (values: any) => {
    const errors: Record<string, any> = {};

    const requiredFields = formConfig.fields
      .flat()
      .filter((field: any) => field.isRequired);

    requiredFields.forEach((field: any) => {
      if (!values[field.name]) {
        errors[field.name] = 'Required Field';
      }
    });

    if (values.content && values.content.includes('googleusercontent.com')) {
      errors.content =
        'The article you are trying to submit contains images which were copied from the web. To avoid issues when displying the image, please use the upload image function instead';
    }

    if (values.preview && values.preview.includes('googleusercontent.com')) {
      errors.preview =
        'The article you are trying to submit contains images which were copied from the web. To avoid issues when displying the image, please use the upload image function instead';
    }

    if (values.isPublished && !values.publishDate) {
      errors.publishDate = 'Required field for the published article';
    }

    if (Array.isArray(values.widgets)) {
      const widgetsError = [] as any[];
      const widgetsNames = {} as any;

      values.widgets.forEach((widget: any, index: number) => {
        const widgetError = {} as any;

        if (!widget.name) {
          widgetError.name = 'Should not be empty';
        } else {
          if (widgetsNames[widget.name]) {
            widgetError.name = 'Should be unique';
          }

          widgetsNames[widget.name] = true;
        }

        if (!widget.code) {
          widgetError.code = 'Should not be empty';
        }
        if (Object.keys(widgetError).length > 0) {
          widgetsError[index] = widgetError;
        }
      });

      if (widgetsError.some((item: any) => !!item)) {
        errors.widgets = widgetsError;
      }
    }
    return errors;
  };
}

const mapState = (state: IReduxStoreState): IStateProps => ({
  article: state.article.article,
  categories: state.article.categories,
  hashtags: state.hashtags.list,
});

const mapDispatch = {
  createArticle,
  editArticle,
  fetchArticle,
  fetchCategories,
  fetchHashtags,
  resetArticleForm,
};

export default connect(mapState, mapDispatch)(ArticleForm);
